/**
 * Public read-only deck. No auth, accessed via /d/:token.
 *
 * Renders the plan as an interactive slide deck with keyboard nav and
 * an optional feedback widget so the wider team can react / comment
 * without needing to sign in.
 */

import { useCallback, useEffect, useState } from 'react';
import { Logo } from '../App';
import { IconArrowLeft, IconArrowRight, IconHeart, IconStar, IconX, IconChat, IconClose } from '../components/Icon';

interface DeckTheme {
  id: string;
  title: string;
  colorHue: number;
  ideas: Array<{ id: string; text: string }>;
}

interface DeckSession {
  id: string;
  name: string;
  description: string | null;
  date: string;
  startMinute: number;
  endMinute: number;
  ownerId: string | null;
  colorHue: number;
  themeIds: string[];
}

interface DeckResponse {
  board: {
    id: string;
    name: string;
    tagline: string | null;
    startsOn: string | null;
    endsOn: string | null;
    visualTheme: string | null;
  };
  members: Array<{ id: string; name: string; avatarHue: number }>;
  themes: DeckTheme[];
  sessions: DeckSession[];
}

export function PublicDeck({ token }: { token: string }) {
  const [data, setData] = useState<DeckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [slide, setSlide] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/deck/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(r.status === 404 ? 'This deck is not available.' : await r.text());
        }
        return (await r.json()) as DeckResponse;
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (!data) return;
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown')
        setSlide((s) => Math.min(slideCount(data) - 1, s + 1));
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') setSlide((s) => Math.max(0, s - 1));
      if (e.key === 'Home') setSlide(0);
      if (e.key === 'End') setSlide(slideCount(data) - 1);
    },
    [data],
  );

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-ink p-6 text-center text-cream-2">
        <Logo size={40} />
        <p className="text-sm">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Logo size={40} />
      </div>
    );
  }

  const total = slideCount(data);

  return (
    <div
      className="relative min-h-screen overflow-hidden text-cream-1"
      style={{
        background: `radial-gradient(ellipse at 30% 0%, hsl(28,40%,16%) 0%, hsl(15,30%,6%) 60%)`,
      }}
    >
      <header className="absolute left-0 right-0 top-0 flex items-center justify-between p-4">
        <div className="inline-flex items-center gap-2 text-xs text-cream-3">
          <Logo size={18} />
          SynFolio · {data.board.name}
        </div>
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
        >
          <IconChat size={14} />
          <span className="hidden sm:inline">Leave feedback</span>
        </button>
      </header>

      <main className="flex min-h-screen items-center justify-center p-6 pt-16">
        <div className="w-full max-w-4xl">
          <Slide data={data} slide={slide} />
        </div>
      </main>

      <footer className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-4">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setSlide((s) => Math.max(0, s - 1))}
          disabled={slide === 0}
        >
          <IconArrowLeft size={16} />
        </button>
        <span className="text-xs text-cream-3">
          {slide + 1} / {total}
        </span>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setSlide((s) => Math.min(total - 1, s + 1))}
          disabled={slide === total - 1}
        >
          <IconArrowRight size={16} />
        </button>
      </footer>

      {feedbackOpen && (
        <FeedbackPanel
          token={token}
          sessions={data.sessions}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}

function slideCount(d: DeckResponse): number {
  const dates = uniqueDates(d.sessions);
  // Title + one per day + themes overview + closing
  return 1 + dates.length + (d.themes.length > 0 ? 1 : 0) + 1;
}

function uniqueDates(s: DeckSession[]): string[] {
  return Array.from(new Set(s.map((x) => x.date))).sort();
}

function Slide({ data, slide }: { data: DeckResponse; slide: number }) {
  const dates = uniqueDates(data.sessions);
  const hasThemes = data.themes.length > 0;

  if (slide === 0) {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <h1 className="text-5xl font-medium sm:text-7xl">{data.board.name}</h1>
        {data.board.tagline && (
          <p className="max-w-2xl text-lg text-cream-2">{data.board.tagline}</p>
        )}
        {(data.board.startsOn || data.board.endsOn) && (
          <p className="text-sm text-cream-3">
            {data.board.startsOn} — {data.board.endsOn}
          </p>
        )}
      </div>
    );
  }

  if (hasThemes && slide === 1) {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl text-cream-2">Themes</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {data.themes.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl border border-cream-4/30 p-4"
              style={{
                background: `linear-gradient(135deg, hsla(${t.colorHue},70%,55%,0.18), hsla(${t.colorHue},70%,35%,0.05))`,
                borderLeft: `3px solid hsl(${t.colorHue}, 70%, 60%)`,
              }}
            >
              <h3 className="text-base">{t.title}</h3>
              <ul className="mt-2 flex flex-col gap-1 text-sm text-cream-2">
                {t.ideas.slice(0, 6).map((i) => (
                  <li key={i.id}>— {i.text}</li>
                ))}
                {t.ideas.length > 6 && (
                  <li className="text-cream-3">+ {t.ideas.length - 6} more</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const dayOffset = hasThemes ? 2 : 1;
  const dayIdx = slide - dayOffset;
  if (dayIdx >= 0 && dayIdx < dates.length) {
    const date = dates[dayIdx]!;
    const dayLabel = new Date(date + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    const sessions = data.sessions
      .filter((s) => s.date === date)
      .sort((a, b) => a.startMinute - b.startMinute);
    return (
      <div className="flex flex-col gap-4">
        <header>
          <p className="text-xs uppercase tracking-wider text-cream-3">{dayLabel}</p>
          <h2 className="mt-1 text-3xl">Day {dayIdx + 1}</h2>
        </header>
        <ul className="flex flex-col gap-3">
          {sessions.map((s) => {
            const owner = data.members.find((m) => m.id === s.ownerId);
            const themes = s.themeIds
              .map((id) => data.themes.find((t) => t.id === id))
              .filter((t): t is DeckTheme => !!t);
            return (
              <li
                key={s.id}
                className="rounded-xl border border-cream-4/30 bg-black/30 p-4"
                style={{ borderLeft: `4px solid hsl(${s.colorHue}, 70%, 60%)` }}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-lg font-medium">{s.name}</h3>
                  <span className="font-mono text-xs text-cream-3">
                    {fmt(s.startMinute)} – {fmt(s.endMinute)}
                  </span>
                </div>
                {s.description && (
                  <p className="mt-1 text-sm text-cream-2">{s.description}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-cream-3">
                  {owner && <span>{owner.name}</span>}
                  {themes.map((t) => (
                    <span
                      key={t.id}
                      className="rounded-full border px-2 py-0.5"
                      style={{
                        borderColor: `hsla(${t.colorHue}, 70%, 60%, 0.4)`,
                        background: `hsla(${t.colorHue}, 70%, 60%, 0.15)`,
                      }}
                    >
                      {t.title}
                    </span>
                  ))}
                </div>
              </li>
            );
          })}
          {sessions.length === 0 && (
            <li className="text-sm text-cream-3">Nothing scheduled this day.</li>
          )}
        </ul>
      </div>
    );
  }

  // Closing slide
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <h2 className="text-4xl">Let's make it happen</h2>
      <p className="text-sm text-cream-3">
        Built with SynFolio · {data.board.name}
      </p>
    </div>
  );
}

function FeedbackPanel({
  token,
  sessions,
  onClose,
}: {
  token: string;
  sessions: DeckSession[];
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [sessionId, setSessionId] = useState<string>('');
  const [sent, setSent] = useState<null | 'ok' | string>(null);
  const [busy, setBusy] = useState(false);

  async function react(kind: 'love' | 'curious' | 'pass') {
    setBusy(true);
    try {
      const r = await fetch(`/api/deck/${encodeURIComponent(token)}/feedback`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ kind, name: name || undefined, sessionId: sessionId || undefined }),
      });
      if (!r.ok) throw new Error(await r.text());
      setSent('ok');
      setTimeout(() => setSent(null), 1500);
    } catch (e) {
      setSent(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function comment() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/deck/${encodeURIComponent(token)}/feedback`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind: 'comment',
          body: body.trim(),
          name: name || undefined,
          sessionId: sessionId || undefined,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      setBody('');
      setSent('ok');
      setTimeout(() => setSent(null), 1500);
    } catch (e) {
      setSent(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="m-3 flex w-full max-w-md flex-col gap-3 rounded-2xl border border-cream-4/30 bg-ink p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Leave feedback</h3>
          <button type="button" className="btn btn-ghost p-1" onClick={onClose}>
            <IconClose size={14} />
          </button>
        </header>
        <input
          className="input"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <select
          className="input"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
        >
          <option value="">About the whole plan</option>
          {sessions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.date} · {s.name}
            </option>
          ))}
        </select>
        <div className="grid grid-cols-3 gap-2">
          <button type="button" disabled={busy} className="btn btn-ghost inline-flex items-center justify-center gap-1.5" onClick={() => react('love')}>
            <IconHeart size={14} /> Love
          </button>
          <button type="button" disabled={busy} className="btn btn-ghost inline-flex items-center justify-center gap-1.5" onClick={() => react('curious')}>
            <IconStar size={14} /> Curious
          </button>
          <button type="button" disabled={busy} className="btn btn-ghost inline-flex items-center justify-center gap-1.5" onClick={() => react('pass')}>
            <IconX size={14} /> Pass
          </button>
        </div>
        <textarea
          className="input min-h-[80px]"
          placeholder="Add a comment…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !body.trim()}
          onClick={comment}
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
        {sent === 'ok' && <p className="text-xs text-leaf">Thanks!</p>}
        {sent && sent !== 'ok' && <p className="text-xs text-rust">{sent}</p>}
      </div>
    </div>
  );
}

function fmt(m: number): string {
  const h = Math.floor(m / 60).toString().padStart(2, '0');
  const mm = (m % 60).toString().padStart(2, '0');
  return `${h}:${mm}`;
}
